import type { EvolutionsSubsection } from "./evolutionsMixpanel";
import { withMixpanel } from "./mixpanelClient";
import { mixpanelTrack } from "./mixpanelTrack";
import type { PlayersSubsection } from "./playersMixpanel";

// Compare-players client-side Mixpanel events.
//
// Unlike the other section helpers (sbcMixpanel.ts, playersMixpanel.ts, ...)
// this file has NO `SECTION` constant. Players are staged for comparison from
// shared components — `useCompareToggle` (player grids) and the DetailedPlayer
// toolbar — that render under Players, Evolutions and Squad Builder alike, so
// the origin has to be threaded to the call site either way. Four near-identical
// copies of the same fn (one per section file, the way
// `added_player_to_evolution_builder` is done) would buy nothing here.
//
// "Compare Players" is itself a valid origin: the comparison's own search view
// adds players too.

export const COMPARE_SECTION = "Compare Players";

/** Routed `/compare` vs. the CompareWidget's bottom sheet (expanded or not). */
export type CompareSubsection = "Page" | "Sheet";

/** Where the player was staged from — section + its own subsection scale. */
export type CompareOrigin =
  | { section: "Players"; subsection: PlayersSubsection }
  | { section: "Evolutions"; subsection: EvolutionsSubsection }
  | { section: "Squad Builder"; subsection: "Normal" }
  | { section: "My Club"; subsection: "Player Recommendations" }
  | { section: typeof COMPARE_SECTION; subsection: CompareSubsection };

/**
 * Which control staged the player:
 * - `player_view` — the "Compare +" button on the DetailedPlayer toolbar
 * - `card` — the compare toggle overlaid on a player card in a grid/list
 * - `search` — a search result inside the comparison's add view
 * - `suggestion` — a suggested player inside that same view (the suggestions
 *   thunk's payload). Split from `search` on purpose: it's the only way to tell
 *   whether the suggestion machinery (price cap, role filter, cache) earns its
 *   keep or everyone just types a name.
 */
export type CompareAddSource = "player_view" | "card" | "search" | "suggestion";

/** Modes of the PlayStyles/Attributes toggle. `compact` is currently disabled. */
export type CompareMode = "list" | "grouped" | "compact";

/**
 * `player_count` is the size of the comparison AFTER the add — so it counts
 * 2, 3, 4… as the set grows.
 *
 * Read it as a FUNNEL, not a breakdown: every comparison that reaches 3 also
 * fired at 2, so an Insights breakdown will always show 2 > 3 > 4 and tells you
 * nothing. The question "how often do people compare more than 2" is the
 * conversion rate of a funnel stepping `player_count = 2` → `player_count = 3`
 * (window ~1h; the slice is in-memory, so one comparison ≈ one page-life).
 */
export const trackAddedPlayerToCompare = (params: {
  origin: CompareOrigin;
  source: CompareAddSource;
  player_count: number;
}) => {
  mixpanelTrack("added_player_to_compare", {
    section: params.origin.section,
    subsection: params.origin.subsection,
    source: params.source,
    player_count: params.player_count,
    action_type: "user_investment",
  });
};

// Fired when the comparison is opened. `player_count` here is an OPEN-TIME
// SNAPSHOT — someone who opens at 2 and adds a 3rd inside the sheet stays
// recorded as 2 forever. Use it for "did they open at all"; use the
// added_player_to_compare funnel for comparison depth.
export const trackViewContent = (params: {
  subsection: CompareSubsection;
  player_count: number;
}) => {
  mixpanelTrack("view_content", {
    section: COMPARE_SECTION,
    subsection: params.subsection,
    content_type: "player",
    new_content: false,
    player_count: params.player_count,
    action_type: "core",
  });
};

// Everyone starts in `list`, so this measures "does anyone ever touch the
// toggle", NOT which mode people prefer — a near-zero switch rate means the
// toggle is dead weight, which is the actionable part. Measuring true
// preference needs a randomised initial mode (see experiments/clientExperiments.ts).
export const trackChangedCompareMode = (params: {
  subsection: CompareSubsection;
  value: CompareMode;
  player_count: number;
}) => {
  mixpanelTrack("changed_compare_mode", {
    section: COMPARE_SECTION,
    subsection: params.subsection,
    value: params.value,
    player_count: params.player_count,
    action_type: "browse",
  });
  // Last picked mode on the profile — unlike the event stream this reads as
  // "which mode does this user sit in", and (with the localStorage persistence)
  // it stays true across sessions.
  withMixpanel((mp) => mp.people.set({ compare_mode: params.value }));
};
